import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Download, Copy, Award, Sparkles, Clock, Layers } from "lucide-react";
import { useState } from "react";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";
import { person, projects } from "../lib/data";
import { EASE_OUT, fadeUp, fadeUpSubtle, popIn, staggerParent } from "../lib/motion";

const CREDITS = `Nirav Nayak — Portfolio Vol. 03

Role: Sole designer & builder
Design: Figma · FigmaMake · Custom kinetic type system
Code: React 19 · Tailwind · Framer Motion · Lenis smooth scroll · FastAPI · MongoDB · Resend
Motion: Emil Kowalski-style — transform + opacity, snappy easing
Aesthetic: Brutalist-tech · acid-lime accent · Instrument Serif italic · Bricolage Grotesque

Contact: niravk.naik@gmail.com · Toronto, Canada`;

const Row = ({ label, children }) => (
  <div className="grid grid-cols-1 md:grid-cols-12 gap-4 py-6 border-b border-[color:var(--nn-border)]">
    <div className="md:col-span-3 font-mono text-[10px] uppercase tracking-widest text-[color:var(--nn-muted)]">
      {label}
    </div>
    <div className="md:col-span-9 text-base md:text-lg text-[color:var(--nn-text)]/90 leading-relaxed">
      {children}
    </div>
  </div>
);

const Press = () => {
  const [copied, setCopied] = useState(false);

  const copyCredits = async () => {
    try {
      await navigator.clipboard.writeText(CREDITS);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch (e) {
      /* ignore */
    }
  };

  return (
    <motion.main
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      data-testid="press-page"
    >
      <Navigation />

      {/* Hero */}
      <section className="pt-32 md:pt-40 pb-14 md:pb-20 border-b border-[color:var(--nn-border)]">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10">
          <motion.div {...fadeUp(0)} className="flex items-center gap-3 mb-6">
            <span className="chip chip-accent">
              <Award size={12} /> Press · SOTD Submission
            </span>
            <span className="chip">Portfolio Vol. 03</span>
            <span className="chip">2026</span>
          </motion.div>
          <motion.h1 {...fadeUp(0.05)} className="h-display text-[14vw] md:text-[9vw] lg:text-[7.5vw] leading-[0.85] tracking-tighter mb-8">
            The <span className="font-serif-i text-[color:var(--nn-muted)]">press</span> kit.
          </motion.h1>
          <motion.p {...fadeUp(0.1)} className="max-w-2xl text-lg md:text-2xl text-[color:var(--nn-muted)] leading-snug font-serif-i mb-10">
            Everything you need to feature, review, or write about this portfolio — hero clip, high-res
            screenshots, credits, and a direct line to Nirav.
          </motion.p>

          <motion.div {...fadeUp(0.15)} className="flex flex-wrap items-center gap-3">
            <a
              href={`mailto:${person.email}?subject=Portfolio%20feature%20-%20Nirav%20Nayak`}
              data-cursor="hover"
              data-testid="press-contact"
              className="group inline-flex items-center gap-3 pl-6 pr-2 py-2 rounded-full bg-[color:var(--nn-accent)] text-[color:var(--nn-accent-ink)] font-medium hover:gap-4 transition-all"
            >
              Reach the designer
              <span className="w-10 h-10 rounded-full grid place-items-center bg-[color:var(--nn-accent-ink)] text-[color:var(--nn-accent)]">
                <ArrowUpRight size={16} />
              </span>
            </a>
            <button
              onClick={copyCredits}
              data-cursor="hover"
              data-testid="press-copy-credits"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-[color:var(--nn-border)] hover:border-[color:var(--nn-accent)] transition-colors"
            >
              <Copy size={14} /> {copied ? "Credits copied" : "Copy credits blurb"}
            </button>
          </motion.div>
        </div>
      </section>

      {/* Fact sheet */}
      <section className="py-16 md:py-24 border-b border-[color:var(--nn-border)]">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10">
          <div className="section-label mb-6">◆ At a glance</div>
          <div>
            <Row label="Designer">{person.name} — {person.role}, based in {person.location}</Row>
            <Row label="Portfolio">Vol. 03 · Single-page brutalist-tech aesthetic with acid-lime accent, custom kinetic type, and password gate.</Row>
            <Row label="Case studies">
              <ul className="space-y-1">
                {projects.map((p) => (
                  <li key={p.slug}>
                    <Link to={`/work/${p.slug}`} data-cursor="hover" className="link-underline">
                      ({p.index}) {p.title} — {p.subtitle}
                    </Link>
                  </li>
                ))}
              </ul>
            </Row>
            <Row label="Stack">
              React 19 · Tailwind · Framer Motion · Lenis · FastAPI · MongoDB · Resend
            </Row>
            <Row label="Signature moves">
              Custom lime particle cursor trail · WCAG-AA in dark + light · Lenis smooth momentum ·
              Draggable stickers · Reflex-arcade playground · Editorial numbered chapters
            </Row>
            <Row label="Contact">
              <a href={`mailto:${person.email}`} data-cursor="hover" className="link-underline">{person.email}</a>
              {" · "}
              <a href={person.linkedin} target="_blank" rel="noreferrer" data-cursor="hover" className="link-underline">LinkedIn</a>
            </Row>
          </div>
        </div>
      </section>

      {/* Hero clip placeholder */}
      <section className="py-16 md:py-24 border-b border-[color:var(--nn-border)]">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10">
          <div className="section-label mb-6">◆ Hero clip</div>
          <div className="spotlight-frame relative bg-[color:var(--nn-elev)] aspect-video overflow-hidden">
            <div className="absolute inset-0 grid place-items-center text-center px-6">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--nn-accent-text)] mb-4">
                  ▚ 20-second hero reel · MP4 · 3840×2160
                </div>
                <div className="h-display text-3xl md:text-5xl mb-2">Coming soon</div>
                <div className="text-sm text-[color:var(--nn-muted)] max-w-md mx-auto">
                  Drop the reel URL into <code className="font-mono text-[color:var(--nn-accent-text)]">/pages/Press.jsx</code> to
                  enable inline playback.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Screenshots gallery */}
      <section className="py-16 md:py-24 border-b border-[color:var(--nn-border)]">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10">
          <div className="section-label mb-6">◆ High-res screenshots</div>
          <motion.div {...staggerParent(0.06)} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { src: projects[0].assets.simpleMode, alt: "Lumina Editor — Simple mode", caption: "Lumina Editor · Simple mode" },
              { src: projects[0].assets.advancedMode, alt: "Lumina Editor — Advanced mode", caption: "Lumina Editor · Advanced mode" },
              { src: projects[0].assets.history, alt: "Named History panel", caption: "Named History panel" },
              { src: projects[1].assets.liveEvents, alt: "Verity live monitoring", caption: "Verity · Live monitoring" },
              { src: projects[1].assets.codeEditor, alt: "Verity collaboration suite", caption: "Verity · Collaboration suite" },
              { src: projects[1].assets.rateCandidate, alt: "Verity rate candidate", caption: "Verity · Rate candidate" },
            ].map((s, i) => (
              <motion.figure key={s.src} {...popIn(i * 0.05)} className="spotlight-frame group">
                <div className="relative aspect-video bg-[color:var(--nn-elev)] overflow-hidden">
                  <img
                    src={s.src}
                    alt={s.alt}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-[1200ms] group-hover:scale-[1.02]"
                  />
                </div>
                <figcaption className="flex items-center justify-between gap-4 px-5 py-3 bg-[color:var(--nn-elev)]/80 border-t border-[color:var(--nn-border)]">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--nn-muted)]">
                    {s.caption}
                  </div>
                  <a
                    href={s.src}
                    download
                    data-cursor="hover"
                    className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-[color:var(--nn-accent-text)] hover:text-[color:var(--nn-text)] transition-colors"
                  >
                    <Download size={12} /> Full-res
                  </a>
                </figcaption>
              </motion.figure>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Credits blurb */}
      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10">
          <div className="section-label mb-6">◆ Credits blurb</div>
          <div className="grid md:grid-cols-12 gap-6 items-start">
            <div className="md:col-span-8">
              <motion.pre
                {...fadeUpSubtle(0)}
                className="whitespace-pre-wrap font-mono text-sm md:text-[15px] leading-relaxed p-6 md:p-8 rounded-2xl border border-[color:var(--nn-border)] bg-[color:var(--nn-surface)]/60 text-[color:var(--nn-text)]/90"
                data-testid="press-credits-block"
              >
                {CREDITS}
              </motion.pre>
            </div>
            <div className="md:col-span-4 space-y-4">
              <motion.div {...fadeUpSubtle(0.05)} className="p-5 rounded-2xl border border-[color:var(--nn-border)] bg-[color:var(--nn-surface)]/50">
                <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-[color:var(--nn-accent-text)] mb-3">
                  <Sparkles size={12} /> For editors
                </div>
                <p className="text-sm text-[color:var(--nn-text)]/85 leading-relaxed">
                  Feel free to quote, screenshot, or embed. All assets on this page are cleared
                  for editorial coverage.
                </p>
              </motion.div>
              <motion.div {...fadeUpSubtle(0.1)} className="p-5 rounded-2xl border border-[color:var(--nn-border)] bg-[color:var(--nn-surface)]/50">
                <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-[color:var(--nn-accent-text)] mb-3">
                  <Clock size={12} /> Response time
                </div>
                <p className="text-sm text-[color:var(--nn-text)]/85 leading-relaxed">
                  Nirav responds within 24 hours to press inquiries. Include your outlet and
                  deadline in the subject.
                </p>
              </motion.div>
              <motion.div {...fadeUpSubtle(0.15)} className="p-5 rounded-2xl border border-[color:var(--nn-border)] bg-[color:var(--nn-surface)]/50">
                <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-[color:var(--nn-accent-text)] mb-3">
                  <Layers size={12} /> Under the hood
                </div>
                <p className="text-sm text-[color:var(--nn-text)]/85 leading-relaxed">
                  Motion presets, illustration set, and gate flow available on request for
                  technical write-ups.
                </p>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </motion.main>
  );
};

export default Press;
